import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import PasswordField from "../common/passwordField/passwordField";
import { ChevronRight, ShieldCheck, Store, UserPlus } from "lucide-react";
import logo from "../../images/LogoFullText.png";
import "./signinPage.css";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import { clearTokens, setTokens } from "../../auth/tokenManager";

const SignInPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);
    const [invalidFields, setInvalidFields] = useState(new Set());

    const { setUserData, language, sessionExpired, clearSessionExpired } = useContext(CombinedContext);
    const navigate = useNavigate();
    const location = useLocation();

    // ProtectedRoute sends us here with the page the user was actually after
    // (e.g. /checkout from the activation email). Only same-origin paths are
    // honoured, so a crafted `from` can't bounce anyone off-site.
    const redirectTo = (() => {
        const from = location.state?.from;
        const path = typeof from === "string" ? from : from?.pathname ? `${from.pathname}${from.search || ""}` : null;
        if (!path || !path.startsWith("/") || path.startsWith("//")) return null;
        return path;
    })();

    const showBanner = (type, text, duration = 4000) => {
        setBanner({ type, text });
        setExiting(false);
        setTimeout(() => setExiting(true), duration - 250);
        setTimeout(() => {
            setBanner(null);
            setExiting(false);
        }, duration);
    };

    const getLoginError = (details) => {
        if (details?.includes("Incorrect username or password") || details?.includes("Invalid login")) {
            return { es: "Correo o contraseña incorrectos. Inténtalo de nuevo.", en: "That email or password isn't right. Please try again." };
        }
        if (details?.includes("User is not confirmed")) {
            return { es: "Tu cuenta aún no está verificada. Revisa tu correo para confirmarla.", en: "Your account isn't verified yet. Check your email to confirm it." };
        }
        if (details?.includes("User does not exist")) {
            return { es: "No encontramos una cuenta con ese correo.", en: "We couldn't find an account with that email." };
        }
        return { es: "No pudimos iniciar tu sesión. Inténtalo de nuevo.", en: "We couldn't sign you in. Please try again." };
    };

    /* The banner says what's wrong; these say where. A flagged field clears
       as soon as it's touched, so the red only ever marks what's still open. */
    const clearInvalid = (field) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

    const handleLogin = async () => {
        /* An empty submit used to go all the way to Cognito and come back as
           "that email or password isn't right" — answer it here instead. */
        const missing = new Set();
        if (!email) missing.add("email");
        if (!password) missing.add("password");

        if (missing.size > 0) {
            setInvalidFields(missing);
            showBanner("error", language === "es" ? "Escribe tu correo y contraseña para continuar." : "Enter your email and password to continue.");
            return;
        }

        setInvalidFields(new Set());
        setLoading(true);

        try {
            const data = await usersRequest.login({ email, password });

            const formattedData = {
                ...data,
                cognitoTokens: {
                    IdToken: data.cognitoTokens?.IdToken || data.IdToken,
                    AccessToken: data.cognitoTokens?.AccessToken || data.AccessToken,
                    RefreshToken: data.cognitoTokens?.RefreshToken || data.RefreshToken,
                },
            };

            setUserData(formattedData);

            const storage = rememberMe ? localStorage : sessionStorage;

            // Drop anything left over from a previous session first: a stale
            // localStorage token would otherwise shadow a session-only login.
            clearTokens();
            setTokens(
                {
                    idToken: formattedData.cognitoTokens.IdToken,
                    accessToken: formattedData.cognitoTokens.AccessToken,
                    refreshToken: formattedData.cognitoTokens.RefreshToken,
                },
                { storage }
            );
            storage.setItem("user", JSON.stringify(formattedData.user));
            if (formattedData.shop) {
                storage.setItem("shop", JSON.stringify(formattedData.shop));
            }

            if (redirectTo) {
                return navigate(redirectTo, { replace: true });
            }

            if (
                formattedData.user?.role === "admin" ||
                formattedData.user?.role === "owner" ||
                formattedData.user?.role === "staff"
            ) {
                return navigate("/calendar");
            }

            navigate("/");
        } catch (err) {
            console.error("Login failed:", err);
            // setError(err.message || "Invalid email or password");
            const msg = getLoginError(err.details);
            showBanner("error", language === "es" ? msg.es : msg.en);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleLogin();
    };

    const t = translations[language].signIn;

    // Arriving here because a session lapsed should say so, not look like a
    // random logout.
    useEffect(() => {
        if (!sessionExpired) return;
        showBanner("error", t.sessionExpired, 6000);
        clearSessionExpired?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionExpired]);

    return (
        <div className="sf-auth-page sf-si-root">
            <div className="sf-auth-shell">
                <div className="sf-auth-card">
                    <img src={logo} alt="Sin Fila" className="sf-auth-logo" />

                    <div className="sf-auth-head">
                        <h1 className="sf-auth-title">{t.title}</h1>
                        <p className="sf-auth-sub">{t.subtitle}</p>
                    </div>

                    {/* Email */}
                    <div className="sf-auth-field">
                        <label className="sf-auth-label" htmlFor="sf-si-email">{t.email}</label>
                        <input
                            id="sf-si-email"
                            className={`sf-auth-input${invalidClass("email")}`}
                            type="email"
                            placeholder={t.email}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                clearInvalid("email");
                            }}
                            onKeyDown={handleKeyDown}
                            autoComplete="email"
                        />
                    </div>

                    {/* Password */}
                    <div className="sf-auth-field">
                        <div className="sf-auth-label-row">
                            <label className="sf-auth-label">{t.password}</label>
                            <Link to="/forgot-password" className="sf-auth-link-sm">
                                {t.forgotPassword}
                            </Link>
                        </div>
                        <PasswordField
                            value={password}
                            onChange={(value) => {
                                setPassword(value);
                                clearInvalid("password");
                            }}
                            placeholder={t.password}
                            autoComplete="current-password"
                            onKeyDown={handleKeyDown}
                            className={invalidClass("password").trim()}
                        />
                    </div>

                    {/* Remember me */}
                    <div className="sf-auth-check is-quiet">
                        <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <label htmlFor="rememberMe">{t.rememberMe}</label>
                    </div>

                    {/* Submit */}
                    <button className="sf-auth-cta" onClick={handleLogin} disabled={loading}>
                        {loading && <span className="sf-auth-spinner" aria-hidden="true" />}
                        {loading ? t.loading : t.button}
                    </button>

                    {/* Footer */}
                    <div className="sf-auth-divider">{t.noAccount}</div>

                    <Link to="/register" className="sf-auth-choice">
                        <span className="sf-auth-choice-icon">
                            <UserPlus size={19} strokeWidth={2} />
                        </span>
                        <span className="sf-auth-choice-copy">
                            <strong>{t.createCustomer}</strong>
                            <span>{t.createCustomerDesc}</span>
                        </span>
                        <ChevronRight size={18} className="sf-auth-choice-arrow" />
                    </Link>

                    <Link to="/listyourshop" className="sf-auth-choice">
                        <span className="sf-auth-choice-icon">
                            <Store size={19} strokeWidth={2} />
                        </span>
                        <span className="sf-auth-choice-copy">
                            <strong>{t.registerBusiness}</strong>
                            <span>{t.registerBusinessDesc}</span>
                        </span>
                        <ChevronRight size={18} className="sf-auth-choice-arrow" />
                    </Link>
                </div>

                <p className="sf-auth-trust">
                    <ShieldCheck size={14} strokeWidth={2} />
                    {t.secureNote}
                </p>
            </div>

            {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
        </div>
    );
};

export default SignInPage;
